import { Box } from '@mui/system'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { CommandPalette } from '../CommandPalette'
import LeftPanel from '../LeftPanel'
import { flatNavMenu } from '../LeftPanel/nav'
import { useAccount } from '../../context/account_context'
import { useConfigs } from '../../context/configs_context'

interface Props {
    children: React.ReactNode
}

export enum SidebarMode {
    Normal = 1,
    Small = 2,
}

const Layout = ({ children }: Props) => {
    const [drawerWidth, setDrawerWidth] = useState(240)
    const [headerHeight] = useState(70)
    const [sidebarMode, setSidebarMode] = useState(SidebarMode.Normal)
    const [hideSideBar, setHideSideBar] = useState(0)
    const [hideHeader, setHideHeader] = useState(false)
    const [removePadding, setRemovePadding] = useState(false)
    const location = useLocation()
    const navigate = useNavigate()
    const { accountInfo } = useAccount()
    const { configs } = useConfigs()

    useEffect(() => {
        const sidebarModeFromLS = localStorage.getItem('sidebarMode')
        const value = sidebarModeFromLS
            ? Number(sidebarModeFromLS)
            : SidebarMode.Normal
        handleChangeSidebarMode(value)
    }, [])

    useEffect(() => {
        const formattedPathname = location.pathname.replace(/\/\d+/g, '')
        setHideSideBar(
            [
                '/system/sale/new',
                '/system/sale/edit',
                '/system/sale/budget/new',
                '/system/sale/budget/edit',
                '/system/buy/new',
                '/system/buy/edit',
                '/system/buy/order/new',
                '/system/buy/order/edit',
                '/system/product/load-stock/new',
                '/system/product/transfer-stock/new',
                '/system/product/load-serial/new',
                '/system/product/load-lote/new',
                '/system/dashboard',
                '/system/employee/manage',
                '/system/cashier/manage',
                '/system/sale/credit-note/new',
            ].includes(formattedPathname)
                ? 1
                : ['/'].includes(formattedPathname)
                  ? 2
                  : 0
        )
        setHideHeader(
            [
                '/',
                '/system/sale/new',
                '/system/sale/edit',
                '/system/buy/new',
                '/system/buy/edit',
                '/system/buy/order/new',
                '/system/buy/order/edit',
                '/system/product/load-stock/new',
                '/system/product/transfer-stock/new',
                '/system/product/load-serial/new',
                '/system/product/load-lote/new',
                '/system/sale/budget/new',
                '/system/sale/budget/edit',
                '/system/sale/credit-note/new',
            ].includes(formattedPathname)
        )
        setRemovePadding(['/'].includes(location.pathname))
    }, [location.pathname])

    useEffect(() => {
        if (!accountInfo) return

        const lockOnSaleScreen = localStorage.getItem('lockOnSaleScreen')
        if (lockOnSaleScreen === 'true') {
            navigate('/system/sale/new')
            return
        }

        const nav = flatNavMenu.find(
            (n) => n.link === location.pathname.replace(/\/\d+$/, '')
        )
        if (!nav?.validate(accountInfo, configs)) {
            navigate('/system')
        }
    }, [location.pathname, accountInfo])

    const handleChangeSidebarMode = (value: number) => {
        setSidebarMode(value)
        setDrawerWidth(value === SidebarMode.Normal ? 240 : 60)
        localStorage.setItem('sidebarMode', value.toString())
    }

    if (location.pathname.includes('/system/render-note'))
        return <>{children}</>

    return (
        <>
            <CommandPalette />
            <LeftPanel
                drawerWidth={drawerWidth}
                headerHeight={headerHeight}
                hideSideBar={hideSideBar}
                hideHeader={hideHeader}
                sidebarMode={sidebarMode}
                setSidebarMode={handleChangeSidebarMode}
            />
            <Box
                sx={{
                    width: {
                        lg: hideSideBar
                            ? '100%'
                            : `calc(100% - ${drawerWidth}px)`,
                    },
                    ml: {
                        lg: hideSideBar ? '0' : `${drawerWidth}px`,
                    },
                    minHeight: hideHeader
                        ? '100vh'
                        : `calc(100vh - ${headerHeight}px)`,
                    background: 'var(--app-bg)',
                    p: removePadding ? '0px' : { xs: '12px', md: '30px 40px' },
                }}
            >
                {children}
            </Box>
        </>
    )
}

export default Layout
